import { DocumentType, modelOptions, post, pre, prop, Severity } from "@typegoose/typegoose";
import mongoose from "mongoose";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import { PeriodicProcessLogs } from "./subSchemas/periodicProcessLogs.js";

const SCHEMA_KEY = "PeriodicScriptLogs" as const;

@modelOptions({
    schemaOptions: {
        collection: "PeriodicScriptLogs",
        timestamps: true,
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        _id: true,
    },
})
@post<SchemaBase<PeriodicScriptLogs>>(...addSchemaKeyHook(SCHEMA_KEY))
/**
 * Post-init hook to store the original logsPerProcess state for change detection.
 * This runs after a document is initialized from the database.
 * Uses $locals (Mongoose's recommended property for temporary document-level data).
 * Note: In Typegoose 12, 'init' must be passed as a string, not an array.
 */
@post<PeriodicScriptLogs>("init", function (this: DocumentType<PeriodicScriptLogs>) {
    if (this.logsPerProcess) {
        this.$locals.originalLogsPerProcess = JSON.stringify(this.logsPerProcess);
    }
})
/**
 * Post-query hook to store the original logsPerProcess state for documents loaded via queries.
 * Handles find (array), findOne, and findOneAndUpdate results.
 */
@post<PeriodicScriptLogs>(["find", "findOne", "findOneAndUpdate"], async function (this, doc) {
    if (Array.isArray(doc)) {
        for (const item of doc) {
            if (item?.$locals && item.logsPerProcess) {
                item.$locals.originalLogsPerProcess = JSON.stringify(item.logsPerProcess);
            }
        }
    } else if (doc?.$locals && doc.logsPerProcess) {
        doc.$locals.originalLogsPerProcess = JSON.stringify(doc.logsPerProcess);
    }
})
/**
 * Pre-save hook to detect changes to logsPerProcess (a mixed/Object type).
 * Mongoose cannot automatically detect changes to mixed types, so we compare
 * the current state with the original and mark as modified if changed.
 */
@pre<PeriodicScriptLogs>("save", async function (this: DocumentType<PeriodicScriptLogs>) {
    const original = this.$locals?.originalLogsPerProcess;

    // If this is a new document (never saved), mark as modified
    if (this.isNew && this.logsPerProcess) {
        this.markModified("logsPerProcess");
        return;
    }

    // If baseline exists and current value differs, mark as modified
    if (original !== undefined && JSON.stringify(this.logsPerProcess) !== original) {
        this.markModified("logsPerProcess");
    }
})
/**
 * Post-save hook to update the baseline after a successful save.
 * This prevents unnecessary markModified calls on subsequent saves of the same document instance.
 */
@post<PeriodicScriptLogs>("save", function (this: DocumentType<PeriodicScriptLogs>) {
    const wasLoadedFromDb = this.$locals.originalLogsPerProcess !== undefined;
    const isExplicitlySet = "logsPerProcess" in this && this.logsPerProcess !== undefined;

    if (wasLoadedFromDb || isExplicitlySet) {
        if (this.logsPerProcess) {
            this.$locals.originalLogsPerProcess = JSON.stringify(this.logsPerProcess);
        } else {
            this.$locals.originalLogsPerProcess = undefined;
        }
    }
})
export class PeriodicScriptLogs extends SchemaBase<PeriodicScriptLogs> {
    static schemaKey = SCHEMA_KEY;
    readonly schemaKey = SCHEMA_KEY;

    @prop({ type: () => Date, required: true })
    public date!: Date;

    @prop({ type: () => Number, required: false })
    public errorCount?: number;

    @prop({ type: mongoose.Schema.Types.Mixed, required: true, allowMixed: Severity.ALLOW, default: {} })
    public logsPerProcess: Record<string, PeriodicProcessLogs> = {};

    static getters = {} as const satisfies Record<string, (self: PeriodicScriptLogs) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    constructor(properties?: SchemaConstructorArgs<PeriodicScriptLogs>) {
        super(properties);
        Object.assign(this, properties);
    }
}
